import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useBoolean } from 'react-use'
import { useQueryClient } from '@tanstack/react-query'

import { OrgMembersAPI } from '@makyoapp/makyo-sdk'
import { OrgMemberRole, OrgPermission, TeamMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { MemberStatus } from '@makyoapp/makyo-sdk/models/Members'
import { MemberAdapter, MemberField } from '@makyoapp/makyo-common'
import { ArrowDown16, Block16, Edit16, Refresh16 } from '@makyoapp/makyo-icon/icon'
import {
  AvatarCell,
  Badge,
  DeleteModal,
  getOrgRoleIcon,
  Loading,
  MoreDropdown,
  projectStatusColor,
  Status,
  Table,
  Td,
  Tr,
  userRoleColor,
} from '@makyoapp/makyo-ui'

import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useGetOrgMembers } from '@/resolvers/org/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import { useDateFmt } from '@/hooks/useDateFmt'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useParamId } from '@/hooks/useParamId'
import { useReqError } from '@/hooks/useReqError'
import { ORG_APP_ROUTE, ORG_MEMBER_FIELDS, ORG_ROUTE } from '@/shared/constants'
import { WrapperProps } from '@/shared/routes'
import { EditRoleModal } from '@/components/EditRoleModal'
import { InviteMemberModal } from '@/components/InviteMemberModal'
import Layout from '@/components/Layout'
import { PageUnauthorized } from '@/components/PageUnauthorized'
import { StackedAvatarTd } from '@/components/TdItem'
import { TrNotFound } from '@/components/TrNotFound'

interface OrgMemberItem {
  /**
   * the current field
   */
  field: MemberField
  /**
   * the member object
   */
  member: MemberAdapter
  /**
   * loading state
   */
  isLoading: boolean
  /**
   * on role change callback
   *
   * @param opt
   *
   * is it team role or org role?
   * @param isTeam
   */
  onRoleChange?: (opt: OrgMemberRole, isTeam: boolean) => void
}

/**
 * rendering member field item
 */
function OrgMemberItem({ field, member, isLoading }: OrgMemberItem) {
  const { t } = useTranslation()
  const dateFmtDefault = useDateFmt()

  const [timestamp] = useState(Date.now())
  const userAvatarWithTimestamp =
    member.avatar && !member.avatar.startsWith('blob:') ? `${member.avatar}?t=${timestamp}` : member.avatar

  const updatedAvatarMember = {
    ...member,
    avatar: userAvatarWithTimestamp,
  }

  switch (field) {
    case 'name':
      return (
        <Td>
          {isLoading ? (
            <div className='px-2 [&>div]:block'>
              <Loading fullHeight={false} />
            </div>
          ) : (
            <AvatarCell {...updatedAvatarMember} />
          )}
        </Td>
      )
    case 'status':
      return (
        <Td className='status'>
          <Status className='uppercase' dotColor={projectStatusColor(member.status)}>
            {t(member.status)}
          </Status>
        </Td>
      )
    case 'invited': {
      const val = member[field]
      const joinDate = val ? dateFmtDefault(val) : ''
      return <Td className='date'>{joinDate}</Td>
    }
    case 'joined': {
      const val = member[field]
      const isActive = member.status.toString() === MemberStatus.Active.toLowerCase()
      const joinDate = isActive && val ? dateFmtDefault(val) : ''
      return <Td className='date'>{joinDate}</Td>
    }
    case 'teams':
      return <StackedAvatarTd items={member.teams} />
    case 'orgRole':
      return (
        <Td className='role text-m-sm'>
          <Badge className='uppercase' color={userRoleColor(member.orgRole)}>
            <div className='flex-c1'>
              <span>{getOrgRoleIcon(member.orgRole)}</span>
              <span>{t(member.orgRole)}</span>
            </div>
          </Badge>
        </Td>
      )
    default:
      return <Td className={field}>{member[field]?.toString()}</Td>
  }
}

/**
 * Org Member Page Route
 *
 * @route `o/:orgId/members`
 *
 * @param title
 *
 * @todo refactor this page logic
 */
export default function OrgDetailMember({ title }: WrapperProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentOrgId = useParamId('org') ?? ''

  const queryClient = useQueryClient()

  const { currentOrg } = useCurrentOrgContext()
  const { orgsPerms, updateOrgsPerms, updateOrgs } = useOrgsContext()
  const { userData } = useAuth()
  const printAPIError = useReqError()
  const canViewMember = useOrgPerm(OrgPermission.MembersView)
  const canInvite = currentOrg ? orgsPerms[currentOrg.id]?.[OrgPermission.MembersInviteMembers] : false

  // Local State
  const [leave, setLeave] = useBoolean(false)
  const [isMemberLoading, setIsMemberLoading] = useState('')
  const [editedRole, setEditedRole] = useState<MemberAdapter | undefined>(undefined)
  const [deleteMember, setDeleteMember] = useState<MemberAdapter | undefined>(undefined)
  const [actionLoading, setActionLoading] = useState(false)
  const [show, setShow] = useBoolean(false)

  // Access org member list
  const { data: members, isLoading } = useGetOrgMembers({
    enabled: canViewMember && !!currentOrgId && !!currentOrg,
  })

  const handleEditRole = (newRole: OrgMemberRole | TeamMemberRole) => {
    if (!editedRole || !currentOrg) return

    const memberToEdit = { ...editedRole }
    setEditedRole(undefined)

    // Prevent admin from changing their own role and show a toast notification
    if (memberToEdit.userId === userData?.id && memberToEdit.orgRole === OrgMemberRole.Admin) {
      toasted(t('You cannot change your own role.'))
      return
    }
    // Allow only owners to modify admin roles
    // if (memberToEdit.orgRole === OrgMemberRole.Admin && userData?.orgRole !== OrgMemberRole.Owner) {
    //   return;
    // }

    if (memberToEdit.orgRole === newRole) return

    setIsMemberLoading(memberToEdit.id)

    OrgMembersAPI.ReqUpdateRole(currentOrg.id, memberToEdit.id, newRole as OrgMemberRole)
      .then(async () => {
        // refetch
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.orgMembers, currentOrg.id],
        })

        updateOrgsPerms([currentOrg], () => {
          setIsMemberLoading('')
          toasted(t('Role updated!'))
        })
      })
      .catch((e) => {
        printAPIError(e)
      })
      .finally(() => {
        setIsMemberLoading('')
      })
  }

  const handleLeaveOrg = () => {
    if (!currentOrg) return

    setLeave(false)
    setActionLoading(true)

    OrgMembersAPI.ReqLeave(currentOrg.id)
      .then(() => {
        return updateOrgs().then((orgs) => {
          updateOrgsPerms(orgs)
          const defaultOrg = orgs.find((no) => no.isDefault && no.ownerId === userData?.id) || orgs[0]
          if (defaultOrg) {
            navigate(`${ORG_ROUTE}/${defaultOrg.id}/${ORG_APP_ROUTE}`)
          } else {
            navigate('/')
          }
        })
      })
      .catch((e) => {
        printAPIError(e)
      })
      .finally(() => setActionLoading(false))
  }

  const handleDeleteMember = () => {
    if (!deleteMember || !currentOrg) return

    setDeleteMember(undefined)
    setActionLoading(true)

    OrgMembersAPI.ReqDelete(currentOrg.id, deleteMember.id)
      .then(async () => {
        // refetch
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.orgMembers, currentOrg.id],
        })
      })
      .catch((e) => {
        printAPIError(e)
      })
      .finally(() => {
        setActionLoading(false)
      })
  }

  const handleCancelInvitation = (member: MemberAdapter) => {
    if (!currentOrg) return

    setIsMemberLoading(member.id)
    OrgMembersAPI.ReqDelete(currentOrg.id, member.id)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.orgMembers, currentOrg.id],
        })

        toasted(
          t('Invitation for {{email}} cancelled!', {
            email: member.email,
          })
        )
      })
      .catch(printAPIError)
      .finally(() => setIsMemberLoading(''))
  }

  const handleResendInvitation = (member: MemberAdapter) => {
    if (!currentOrg) return

    setIsMemberLoading(member.id)
    OrgMembersAPI.ReqResendInvitation(currentOrg.id, member.id)
      .then(async () => {
        toasted(
          t('Resend invitation to {{email}} !', {
            email: member.email,
          })
        )
      })
      .catch(printAPIError)
      .finally(() => setIsMemberLoading(''))
  }

  if (!canViewMember) {
    return (
      <Layout title={title}>
        <PageUnauthorized />
      </Layout>
    )
  }

  if (!currentOrg) {
    return null
  }

  const canEditRole = orgsPerms[currentOrg.id]?.[OrgPermission.MembersEditMembers]
  const canInviteMember = orgsPerms[currentOrg.id]?.[OrgPermission.MembersInviteMembers]

  return (
    <Layout
      title={title}
      actions={<>{canInvite && currentOrg && <InviteMemberModal show={show} setShow={setShow} org={currentOrg} />}</>}
    >
      <React.Fragment>
        <Table className='border bdr'>
          <thead>
            <Tr>
              {ORG_MEMBER_FIELDS.map((f) => {
                return <th key={`member-${f}`}>{t(f === 'orgRole' ? 'Role' : f)}</th>
              })}
              <th className='act'></th>
            </Tr>
          </thead>
          <tbody>
            {isLoading || actionLoading ? (
              <Tr>
                <Td colSpan={ORG_MEMBER_FIELDS.length + 1}>
                  <Loading fullHeight={false} />
                </Td>
              </Tr>
            ) : members && members?.length < 1 ? (
              <TrNotFound colSpan={ORG_MEMBER_FIELDS.length + 1} />
            ) : (
              members?.map((member) => {
                const canRemove =
                  member.userId !== userData?.id && orgsPerms[currentOrg.id]?.[OrgPermission.MembersDeleteMembers]

                const canLeave = member.userId === userData?.id && currentOrg?.ownerId !== userData?.id

                const hideMenu = member.orgRole === OrgMemberRole.Owner || !(canRemove || canEditRole || canLeave)

                return (
                  <Tr key={member.id}>
                    {/* Field Row */}
                    {ORG_MEMBER_FIELDS.map((f) => {
                      return (
                        <OrgMemberItem
                          key={`${member.id}-${f}`}
                          field={f}
                          member={member}
                          isLoading={isMemberLoading ? isMemberLoading === member.id : false}
                        />
                      )
                    })}

                    {/* Dropdown Menu */}
                    <Td align='right' className='act'>
                      {!hideMenu && (
                        <MoreDropdown
                          id={`member-more-${member.id}`}
                          placement='bottom-end'
                          menuClassName='text-m-m'
                          portalMenu={true}
                        >
                          {canEditRole && (
                            <MoreDropdown.MenuItem
                              id={`${member.id}-edit-role-act`}
                              className='edit-role-act'
                              onClick={() => {
                                setEditedRole(member)
                              }}
                            >
                              <Edit16 />
                              <span>{t('Edit Role')}</span>
                            </MoreDropdown.MenuItem>
                          )}
                          {member.status === 'pending' && canInviteMember && (
                            <>
                              <MoreDropdown.MenuItem
                                id={`${member.id}-delete-role-act`}
                                className='delete-role-act'
                                onClick={() => {
                                  handleCancelInvitation(member)
                                }}
                              >
                                <Block16 />
                                <span>{t('Cancel Invitation')}</span>
                              </MoreDropdown.MenuItem>
                              <MoreDropdown.MenuItem
                                id={`${member.id}-resend-invite-act`}
                                className='resend-invite-act'
                                onClick={() => {
                                  handleResendInvitation(member)
                                }}
                              >
                                <Refresh16 />
                                <span>{t('Resend Invitation')}</span>
                              </MoreDropdown.MenuItem>
                            </>
                          )}
                          {canLeave && (
                            <MoreDropdown.MenuItem
                              id={`${member.id}-leave-org-act`}
                              className='red leave-org-act'
                              onClick={() => {
                                setLeave(true)
                              }}
                            >
                              <ArrowDown16 />
                              <span>{t('Leave')}</span>
                            </MoreDropdown.MenuItem>
                          )}
                          {canRemove && (
                            <MoreDropdown.MenuItem
                              id={`${member.id}-remove-act`}
                              className='red remove-act'
                              onClick={() => {
                                setDeleteMember(member)
                              }}
                            >
                              <Block16 />
                              <span>{t('Remove')}</span>
                            </MoreDropdown.MenuItem>
                          )}
                        </MoreDropdown>
                      )}
                    </Td>
                  </Tr>
                )
              })
            )}
          </tbody>
        </Table>

        {editedRole && (
          <EditRoleModal
            show={true}
            onHide={() => setEditedRole(undefined)}
            member={editedRole}
            isTeam={false}
            onSubmit={handleEditRole}
          />
        )}

        {leave && (
          <DeleteModal
            show={true}
            onHide={() => setLeave(false)}
            continueLabel={t('Yes, leave')}
            onSubmit={handleLeaveOrg}
            title={t('Leave Organization')}
          >
            <div className='modal-sm'>
              <div className='text-sm mb-2'>{t('Are you sure you’d like to leave this organization?')}</div>
              <div className='text-light'>
                {t(
                  "You will lose access to all organization assets. To gain back access, you'd have to be invited back."
                )}
              </div>
            </div>
          </DeleteModal>
        )}

        {deleteMember && (
          <DeleteModal
            show={true}
            continueLabel={t('Yes, remove')}
            onHide={() => setDeleteMember(undefined)}
            onSubmit={handleDeleteMember}
            title={t('Remove Member')}
            withCancel
          >
            <div className='modal-sm'>
              <div className='text-sm mb-2'>{t("Are you sure you'd like to remove this organization member?")}</div>
              <div className='text-light'>
                {t(
                  'They will no longer have access to any apps or databases belonging to this organization and they will be removed from teams they belong to in this organization.'
                )}
              </div>
            </div>
          </DeleteModal>
        )}
      </React.Fragment>
    </Layout>
  )
}
